import { Client } from '@stomp/stompjs';
import { getAccessToken, isAccessTokenExpiredOrStale } from '@/shared/auth/jwt-storage';
import { readStompConnectHeaders } from '@/shared/ws/crmStompTransport';
import { attachCrmStompTokenLifecycle, createCrmStompLifecycleRefs, createCrmStompWebSocketFactory, ensureFreshAccessTokenForStomp, handleCrmStompAuthFailure, } from '@/shared/ws/crmStompTokenLifecycle';
let client = null;
let lifecycleRefs = createCrmStompLifecycleRefs();
let sockJsUrl = '';
let debug = false;
let consumerCount = 0;
let connectionState = 'idle';
let detachTokenLifecycle = null;
const topicHandlers = new Map();
const activeSubscriptions = new Map();
const connectListeners = new Set();
const stateListeners = new Set();
function notifyConnect() {
    for (const listener of connectListeners) {
        listener();
    }
}
function setConnectionState(next) {
    connectionState = next;
    for (const listener of stateListeners) {
        listener(next);
    }
}
function clearActiveSubscriptions() {
    for (const sub of activeSubscriptions.values()) {
        sub.unsubscribe();
    }
    activeSubscriptions.clear();
}
function resubscribeAllTopics() {
    if (!client?.connected)
        return;
    for (const [topic, handler] of topicHandlers) {
        if (activeSubscriptions.has(topic))
            continue;
        activeSubscriptions.set(topic, client.subscribe(topic, handler));
    }
}
function tearDownSharedClient() {
    lifecycleRefs.stopped.current = true;
    if (client) {
        client.reconnectDelay = 0;
    }
    detachTokenLifecycle?.();
    detachTokenLifecycle = null;
    clearActiveSubscriptions();
    topicHandlers.clear();
    if (client) {
        void client.deactivate().catch(() => undefined);
    }
    client = null;
    consumerCount = 0;
    setConnectionState('idle');
}
function ensureSharedClient() {
    if (client)
        return;
    lifecycleRefs = createCrmStompLifecycleRefs();
    setConnectionState('connecting');
    const stopReconnecting = (reason) => {
        if (lifecycleRefs.stopped.current)
            return;
        lifecycleRefs.stopped.current = true;
        if (client) {
            client.reconnectDelay = 0;
        }
        console.warn('[CRM STOMP shared]', reason);
        setConnectionState('error');
        void client?.deactivate().catch(() => undefined);
    };
    const tryRefreshAuthAfterTransportFailure = () => {
        if (lifecycleRefs.stopped.current || lifecycleRefs.authRefreshInFlight.current)
            return;
        void handleCrmStompAuthFailure(nextClient, lifecycleRefs, stopReconnecting);
    };
    const nextClient = new Client({
        webSocketFactory: createCrmStompWebSocketFactory(sockJsUrl, () => {
            nextClient.connectHeaders = readStompConnectHeaders();
        }),
        connectHeaders: readStompConnectHeaders(),
        reconnectDelay: 5000,
        heartbeatIncoming: 4000,
        heartbeatOutgoing: 4000,
        debug: debug ? (message) => console.debug('[STOMP shared]', message) : () => undefined,
    });
    nextClient.beforeConnect = async (c) => {
        if (lifecycleRefs.stopped.current)
            return;
        const ok = await ensureFreshAccessTokenForStomp(lifecycleRefs);
        if (!ok && isAccessTokenExpiredOrStale(0)) {
            stopReconnecting('STOMP connect skipped — access token refresh failed.');
            c.reconnectDelay = 0;
            throw new Error('STOMP access token refresh failed');
        }
        if (!getAccessToken()) {
            stopReconnecting('STOMP connect skipped — no access token.');
            c.reconnectDelay = 0;
            throw new Error('STOMP access token missing');
        }
        c.connectHeaders = readStompConnectHeaders();
    };
    nextClient.onConnect = () => {
        lifecycleRefs.failureCount.current = 0;
        setConnectionState('connected');
        resubscribeAllTopics();
        notifyConnect();
    };
    nextClient.onStompError = (frame) => {
        const body = frame.headers?.message ?? frame.body ?? '';
        if (/401|unauthorized|authentication/i.test(body)) {
            void handleCrmStompAuthFailure(nextClient, lifecycleRefs, stopReconnecting);
            return;
        }
        lifecycleRefs.failureCount.current += 1;
        if (lifecycleRefs.failureCount.current >= 3) {
            stopReconnecting('STOMP errors persisted — shared connection stopped.');
        }
        else {
            setConnectionState('error');
        }
    };
    nextClient.onDisconnect = () => {
        if (lifecycleRefs.stopped.current)
            return;
        clearActiveSubscriptions();
        if (connectionState === 'connected') {
            setConnectionState('disconnected');
        }
    };
    nextClient.onWebSocketClose = () => {
        if (lifecycleRefs.stopped.current)
            return;
        clearActiveSubscriptions();
        lifecycleRefs.failureCount.current += 1;
        if (isAccessTokenExpiredOrStale(0) || lifecycleRefs.failureCount.current >= 2) {
            tryRefreshAuthAfterTransportFailure();
            return;
        }
        if (connectionState === 'connected') {
            setConnectionState('disconnected');
        }
    };
    nextClient.onWebSocketError = () => {
        lifecycleRefs.failureCount.current += 1;
        if (isAccessTokenExpiredOrStale(0) || lifecycleRefs.failureCount.current >= 1) {
            tryRefreshAuthAfterTransportFailure();
            return;
        }
        setConnectionState('error');
    };
    client = nextClient;
    detachTokenLifecycle = attachCrmStompTokenLifecycle(nextClient, lifecycleRefs);
    try {
        nextClient.activate();
    }
    catch (error) {
        console.error('[CRM STOMP shared] activate failed', error);
        setConnectionState('error');
    }
}
/** Acquire the shared CRM SockJS/STOMP connection (ref-counted). */
export function acquireCrmStompSharedConnection(url, options) {
    sockJsUrl = url;
    debug = Boolean(options?.debug);
    consumerCount += 1;
    ensureSharedClient();
}
/** Release the shared connection; closes when no consumers remain. */
export function releaseCrmStompSharedConnection() {
    consumerCount = Math.max(0, consumerCount - 1);
    if (consumerCount === 0) {
        tearDownSharedClient();
    }
}
export function getCrmStompSharedClient() {
    return client;
}
export function getCrmStompSharedConnectionState() {
    return connectionState;
}
export function onCrmStompSharedConnect(listener) {
    connectListeners.add(listener);
    if (client?.connected) {
        listener();
    }
    return () => connectListeners.delete(listener);
}
export function onCrmStompSharedConnectionState(listener) {
    stateListeners.add(listener);
    listener(connectionState);
    return () => stateListeners.delete(listener);
}
/** Subscribe to a STOMP topic on the shared client. Returns an unsubscribe function. */
export function subscribeCrmStompSharedTopic(topic, handler) {
    topicHandlers.set(topic, handler);
    if (client?.connected) {
        const existing = activeSubscriptions.get(topic);
        existing?.unsubscribe();
        activeSubscriptions.set(topic, client.subscribe(topic, handler));
    }
    return () => {
        topicHandlers.delete(topic);
        const sub = activeSubscriptions.get(topic);
        if (sub) {
            sub.unsubscribe();
            activeSubscriptions.delete(topic);
        }
    };
}
